import React, { useEffect, useState } from "react";
import GuildTabs from "./GuildTabs";
import { motion } from "framer-motion";
import Explore from "./sections/Explore";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import Build from "./sections/Build";
import { useDispatch, useSelector } from "react-redux";
import GuildDetail from "./sections/GuildDetail";
import UpdateGuild from "./sections/UpdateGuild";
import Network from "../../utils/Network";
import AdventureHistory from "../Adventure";
import { setUserInfo } from "../../redux/user/action";
import PageHeader from "../../components/PageHeader";
import GuildBackdrop from "./components/GuildBackdrop";
import { DEFAULT_GUILD_HALL, guildAsset } from "./utils";
import "./guild.css";
import { useAppTranslation } from "../../i18n/useAppTranslation";
type Props = {};

export type GuildTabType = {
  title: string;
  icon: string;
  value: string;
  component: React.ReactNode;
  disabled?: boolean;
  hidden?: boolean;
};

const GuildPage = (props: Props) => {
  const { t } = useAppTranslation(["common", "guild", "navigation", "profile"]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const id = searchParameters.get("id");
  const dispatch = useDispatch();
  const location = useLocation();
  const userInfo = useSelector((state: any) => state.userInfo);
  const isGuildAvailable = Boolean(userInfo?.guild?.id);
  const axios = new Network();
  const [images, setImages] = useState({ images: [""], banners: [""] });
  // The hall behind the page: the one being tried on (hovered in Explore, picked in the
  // builder), else the hall of the guild that is open, else the player's own, else the default.
  const [openHall, setOpenHall] = useState<string | null>(null);
  const [triedHall, setTriedHall] = useState<string | null>(null);
  const handleGetImages = async () => {
    const response = await axios.CallApi({
      url: "/guild/images",
      method: "get",
    });
    setImages(response.data);
  };
  const getMe = async () => {
    const response = await axios.CallApi({
      url: "/player",
      method: "get",
    });
    if (response.ok) {
      dispatch(setUserInfo(response.data));
    }
  };
  const changeQuery = (val: number | string | boolean, name: string) => {
    val ? searchParameters.set(name, val + "") : searchParameters.delete(name);
    setSearchParameters(searchParameters, { replace: true });
  };
  useEffect(() => {
    getMe();
    handleGetImages();
  }, []);

  const Tabs: GuildTabType[] = [
    {
      title: t("common:chat.explore"),
      icon: "/assets/images/icons/scout.png",
      value: "explore",
      // component: <Build />,
      component: <Explore onTryHall={setTriedHall} canBuild={!isGuildAvailable} />,
      disabled: false,
    },
    {
      title: t("navigation:guild"),
      icon: "/assets/images/icons/castle.png",
      value: "my-guild",
      component: <GuildDetail id={userInfo?.guild?.id} onHall={setOpenHall} />,
      disabled: !isGuildAvailable,
    },
    {
      title: t("profile:publicProfile.ranks.raids"),
      icon: "/assets/images/icons/active-raid.png",
      value: "raids",
      component: <AdventureHistory id={userInfo?.guild?.id} />,
      disabled: !isGuildAvailable,
    },

    {
      title: t("navigation:guild"),
      icon: "/assets/images/icons/scout.png",
      value: "guild",
      hidden: true,
      component: id && <GuildDetail id={+id!} onHall={setOpenHall} />,
    },
    {
      title: t("guild:explore.cta.action"),
      icon: "/assets/images/icons/scout.png",
      value: "create",
      hidden: true,
      component: (
        <Build
          images={images}
          onTryHall={setTriedHall}
          onSuccess={() => {
            changeQuery("my-guild", "tab");
            getMe();
          }}
        />
      ),
    },
    {
      title: t("guild:detail.actions.setting"),
      icon: "/assets/images/icons/scout.png",
      value: "setting",
      hidden: true,
      component: (
        <UpdateGuild
          id={id!}
          onTryHall={setTriedHall}
          onSuccess={() => {
            getMe();
          }}
        />
      ),
    },
  ];
  const tabValue = searchParameters.get("tab");

  const value = tabValue;

  // Both writes below are URL *corrections* — filling in the default tab and
  // redirecting a tab that needs an id it doesn't have. They must replace, not
  // push: as a push, arriving at /guild left an extra entry behind, so going
  // back landed on the bare /guild, which re-ran this effect and pushed the tab
  // straight back on. Back appeared completely dead on this page.
  const handleSetValue = () => {
    searchParameters.set(
      "tab",
      userInfo?.guild?.id ? Tabs[1].value : Tabs[0].value
    );

    setSearchParameters(searchParameters, { replace: true });
  };

  useEffect(() => {
    if (!value && location.pathname.includes("/guild")) {
      handleSetValue();
    }
    if (value === "guild" && !id) {
      searchParameters.set("tab", "explore");
      setSearchParameters(searchParameters, { replace: true });
    }
  }, [value, id]);

  // A hall belongs to the view it was set for.
  useEffect(() => {
    setOpenHall(null);
    setTriedHall(null);
  }, [value, id]);

  const activeComponent = Tabs.find((item) => item.value === value)?.component;
  const showsOneGuild = value === "my-guild" || value === "guild";
  const ownHall = isGuildAvailable ? userInfo?.guild?.banner : null;
  const hall = guildAsset(triedHall || (showsOneGuild ? openHall : null) || ownHall, DEFAULT_GUILD_HALL);
  return (
    <>
      {/* Outside the animated wrapper: a transformed parent would stop it filling the screen. */}
      <GuildBackdrop src={hall} />
      <motion.div
        className="gh-page"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{
          opacity: 0,
        }}
      >
        <PageHeader title={t("navigation:guild")} />
        <GuildTabs value={value!} tabs={Tabs} />
        <div className={value === "raids" ? "gh-section gh-panel" : "gh-section"}>{activeComponent}</div>
      </motion.div>
    </>
  );
};

export default GuildPage;
